import {GraphLayout, GraphNode} from '../../../../../components/graph/interface';
import {MaxPathLengthOptionResult} from './requests/use-query-result-max-length-options';
import {useQueryGraphProps} from './use-query-graph-props';
import {GraphPresentationDetails} from './interface';
import {StructuredGraph} from '../../../../../components/graph/graph-logic/structured-graph';

const problematicCytoscapeGraphSize = 500;
const unmanageableSizeCytoscapeGraphSize = 4000;

// Determines if number of nodes is big enough to cause some problems in application performance
export function isGraphProblematic(nodesNumber: number) {
    return nodesNumber > problematicCytoscapeGraphSize;
}

// Determines if num of nodes is big enough to cause major performance problems and break the app
export function isGraphUnmanageable(nodesNumber: number) {
    return nodesNumber > unmanageableSizeCytoscapeGraphSize;
}

export function isQueryResultUnmanageable(maxPathLengthOptions: MaxPathLengthOptionResult) {
    return (
        maxPathLengthOptions.merged[0] &&
        isGraphUnmanageable(maxPathLengthOptions.merged[0].amountOfNodes)
    );
}

export function isQueryResultIncomplete(maxPathLengthOptions: MaxPathLengthOptionResult) {
    return !maxPathLengthOptions.merged.length;
}

export function getNodesFromRawNodes(rawNodeIds: number[], structuredGraph?: StructuredGraph) {
    if (!structuredGraph) {
        return [];
    }

    const hiddenNodesRawIds = new Set(rawNodeIds.map(Number));
    const result: number[] = [];
    structuredGraph.allNodes.forEach((node) => {
        if (node.rawNodeIds.every((rawNodeId) => hiddenNodesRawIds.has(rawNodeId))) {
            result.push(node.id);
        }
    });
    return result;
}

export function parsePresDetailsLayout(
    layout: string,
    isHiddenNodesVisible: boolean,
    isHiddenNode: (id: number) => boolean
): {visibleNodesPositions: string; invisibleNodesPositions: string} {
    const layoutJson = layout ? JSON.parse(layout) : {};

    const visibleNodesPositionsMap: GraphLayout = {};
    const invisibleNodesPositionsMap: GraphLayout = {};

    Object.keys(layoutJson).forEach((key) => {
        if (isHiddenNodesVisible || !isHiddenNode(Number(key))) {
            visibleNodesPositionsMap[key] = layoutJson[Number(key)];
        } else {
            invisibleNodesPositionsMap[key] = layoutJson[Number(key)];
        }
    });
    const visibleNodesPositions = Object.keys(visibleNodesPositionsMap).length
        ? JSON.stringify(visibleNodesPositionsMap)
        : '';
    const invisibleNodesPositions = Object.keys(invisibleNodesPositionsMap).length
        ? JSON.stringify(invisibleNodesPositionsMap)
        : '';

    return {visibleNodesPositions, invisibleNodesPositions};
}

export function persistPositionsByRawNodeId(
    prevNodes: GraphNode[],
    newNodes: GraphNode[],
    previousNodesPositions: string
) {
    const newPositions: GraphLayout = {};
    const positionsByRawNodeId: GraphLayout = {};
    const previousNodesPositionsParsed: GraphLayout = JSON.parse(previousNodesPositions);

    prevNodes.forEach((node) =>
        node.rawNodeIds.forEach((rawNodeId) => {
            positionsByRawNodeId[rawNodeId] = previousNodesPositionsParsed[node.id];
        })
    );

    newNodes.forEach((node) => {
        newPositions[node.id] = positionsByRawNodeId[node.rawNodeIds[0]];
    });

    return newPositions;
}

export function getPresentationDetails(
    graphProps: ReturnType<typeof useQueryGraphProps>
): GraphPresentationDetails {
    return {
        layout: JSON.stringify({
            ...graphProps.layoutAndCameraProps.visibleNodesPositions,
            ...graphProps.layoutAndCameraProps.invisibleNodesPositions
        }),
        layoutAlgorithm: graphProps.layoutAndCameraProps.layoutAlgorithm,
        isLayoutCustom: graphProps.layoutAndCameraProps.isCustomLayout,
        hiddenNodes: graphProps.hiddenNodesProps.hiddenRawNodeIds,
        viewKind: graphProps.graphSettings.viewKind,
        maxPathLength: graphProps.graphSettings.maxPathLength
    };
}
